import React from 'react';
import { FiArrowRight, FiClock, FiUsers, FiStar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Courses = () => {
  const courses = [
    {
      title: "Nursery Program",
      ageGroup: "2-3 Years",
      description: "Foundation building through play-based learning, sensory activities, and social-emotional development in a nurturing environment.",
      features: ["Sensory Play", "Motor Skills", "Social Skills", "Language Foundation"],
      image: "/img/courses/img_26.jpeg",
      color: "from-pink-400 to-rose-500"
    },
    {
      title: "Kindergarten Program",
      ageGroup: "3-4 Years",
      description: "Structured learning with focus on creative expression, pre-reading, pre-math, and character development through engaging activities.",
      features: ["Creative Arts", "Phonics Intro", "Number Concepts", "Character Building"],
      image: "/img/courses/img_25.jpeg",
      color: "from-blue-400 to-indigo-500"
    },
    {
      title: "Pre-K Program",
      ageGroup: "4-5 Years",
      description: "School readiness program with emphasis on academic skills, independence, and preparation for Grade 1 with STEM activities.",
      features: ["School Readiness", "STEM Basics", "Leadership Skills", "Academic Prep"],
      image: "/img/courses/img_16.jpeg",
      color: "from-emerald-400 to-teal-500"
    },
    {
      title: "Primary Program (Grade 1-5)",
      ageGroup: "6-10 Years",
      description: "Comprehensive CBSE curriculum with emphasis on literacy, numeracy, science exploration, and critical thinking skills development.",
      features: ["CBSE Framework", "Subject Mastery", "STEM Learning", "Life Skills"],
      image: "/img/courses/img_15.jpeg",
      color: "from-orange-400 to-amber-500"
    },
    {
      title: "Middle School (Grade 6-7)",
      ageGroup: "11-13 Years",
      description: "Advanced CBSE curriculum with specialized subjects, project-based activities, and preparation for secondary education.",
      features: ["Advanced Curriculum", "Critical Thinking", "Research Projects", "Career Awareness"],
      image: "/img/courses/img_12.jpeg",
      color: "from-purple-400 to-indigo-500"
    }
  ];

  return (
    <section className="py-20 bg-white" id="program">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our Latest{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
              Programs
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Comprehensive programs from Nursery to Grade 7 designed to nurture young minds and 
            prepare your child for lifelong success through quality education and holistic development.
          </p>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-16">
          {courses.map((course, index) => (
            <div
              key={course.title}
              className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              data-aos="fade-up"
              data-aos-delay={index * 150}
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={course.image}
                  alt={`${course.title} - Children learning in age-appropriate activities`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                
                {/* Age Badge */}
                {/* <div className={`absolute top-4 right-4 bg-gradient-to-r ${course.color} text-white px-4 py-2 rounded-full text-sm font-semibold`}>
                  {course.ageGroup}
                </div> */}
              </div>

              {/* Content */}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-emerald-600 transition-colors duration-200">
                  {course.title}
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {course.description}
                </p>

                {/* Features */}
                <div className="space-y-3 mb-6">
                  {course.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <SafeIcon icon={FiStar} className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Meta Info */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-6">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiClock} className="w-4 h-4" />
                    <span>8:30 AM - 3:00 PM</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiUsers} className="w-4 h-4" />
                    <span>Small Groups</span>
                  </div>
                </div>

                {/* CTA */}
                <button className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 rounded-xl font-semibold hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 transform hover:scale-105">
                  Learn More
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center" data-aos="fade-up">
          <a
            href="/academics"
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:from-emerald-700 hover:to-teal-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <span>Explore All Programs for 2026</span>
            <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Courses;